import { ProductLookupComponent } from '../../../../modules/products/components/product-lookup/product-lookup.component';
import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { AvatarModule } from 'primeng/avatar';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { RippleModule } from 'primeng/ripple';

import { LucideAngularModule } from 'lucide-angular';
import { NotificationsPopoverComponent } from '../../../components/notifications-popover/notifications-popover.component';
import { AuthFacade } from '../../../../modules/auth/facades';
import { AuthorizationStateService } from '../../../../modules/auth/services/authorization-state.service';
import { NavigationStateService, PermissionsService } from '../../../services';
import { GuidedOnboardingService } from '../../../onboarding/services/guided-onboarding.service';

@Component({
  selector: 'app-top-bar',
  standalone: true,
  imports: [
    CommonModule,
    ProductLookupComponent,
    RouterModule,
    AvatarModule,
    NotificationsPopoverComponent,
    MenuModule,
    RippleModule,
    LucideAngularModule
  ],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss'
})
export class TopBarComponent {
  private readonly nav = inject(NavigationStateService);
  private readonly authFacade = inject(AuthFacade);

  protected readonly breadcrumb = this.nav.breadcrumb;
  private readonly authorizationState = inject(AuthorizationStateService, { optional: true });
  private readonly permissionsService = inject(PermissionsService);
  private readonly guidedOnboarding = inject(GuidedOnboardingService);

  private readonly user = typeof this.authFacade?.user === 'function'
    ? this.authFacade.user
    : toSignal(this.authFacade.user$, { initialValue: null });
  private readonly company = typeof this.authFacade?.company === 'function'
    ? this.authFacade.company
    : toSignal(this.authFacade.company$, { initialValue: null });
  private readonly companies = typeof this.authFacade?.companies === 'function'
    ? this.authFacade.companies
    : toSignal(this.authFacade.companies$, { initialValue: [] });

  private readonly planAccess = computed(() => {
    if (typeof this.authFacade?.plan === 'function') {
      const plan = this.authFacade.plan();
      if (plan) {
        return plan;
      }
    }
    if (this.authorizationState?.plan) {
      return this.authorizationState.plan();
    }
    return null;
  });

  private readonly commercialAccess = computed(() => {
    if (typeof this.authFacade?.commercialAccess === 'function') {
      const access = this.authFacade.commercialAccess();
      if (access) {
        return access;
      }
    }
    if (this.authorizationState?.commercialAccess) {
      return this.authorizationState.commercialAccess();
    }
    return null;
  });

  readonly subscriptionInfo = computed(() => {
    const slug = (this.planAccess()?.slug || '').toLowerCase().trim();
    const commercialStatus = this.commercialAccess()?.status;

    if (
      slug.includes('enterprise') ||
      slug.includes('avancado') ||
      slug.includes('advanced') ||
      slug.includes('premium')
    ) {
      return {
        tier: 'enterprise',
        label: 'Plano Avançado (Enterprise)',
        ringClass: 'topbar__avatar-ring--enterprise'
      };
    }

    if (
      slug.includes('pro') ||
      slug.includes('controle') ||
      slug.includes('professional') ||
      slug.includes('business')
    ) {
      return {
        tier: 'pro',
        label: 'Plano Pro (Controle)',
        ringClass: 'topbar__avatar-ring--pro'
      };
    }

    if (
      slug.includes('starter') ||
      slug.includes('essencial') ||
      slug.includes('basic') ||
      slug.includes('iniciante')
    ) {
      return {
        tier: 'starter',
        label: 'Plano Starter (Essencial)',
        ringClass: 'topbar__avatar-ring--starter'
      };
    }

    if (commercialStatus === 'trial_active') {
      return {
        tier: 'trial',
        label: 'Período de Testes Ativo',
        ringClass: 'topbar__avatar-ring--trial'
      };
    }

    return {
      tier: 'none',
      label: 'Conta GextorPro',
      ringClass: 'topbar__avatar-ring--none'
    };
  });

  readonly userAvatar =
    'https://primefaces.org/cdn/primeng/images/demo/avatar/asiyajavayant.png';
  readonly userName = computed(() => this.user()?.name ?? 'Usuário');
  readonly userEmail = computed(() => this.user()?.email ?? '');
  readonly companyName = computed(
    () => this.company()?.tradeName ?? this.company()?.legalName ?? null
  );
  readonly canViewCompanySettings = computed(() => this.permissionsService.can('settings', 'view'));
  readonly userMenuItems = computed<MenuItem[]>(() => {
    const accountItems: MenuItem[] = [
      {
        label: 'Meu Perfil',
        icon: 'user',
        routerLink: '/perfil'
      }
    ];

    if (this.canViewCompanySettings()) {
      accountItems.push({
        label: 'Empresa',
        icon: 'building',
        routerLink: '/configuracoes'
      });
    }

    if (this.companies().length > 1) {
      accountItems.push({
        label: 'Trocar empresa',
        icon: 'building-2',
        routerLink: '/auth/select-company'
      });
    }

    if (this.guidedOnboarding.canReplay()) {
      accountItems.push({
        id: 'help-tour',
        label: 'Conhecer o GextorPro',
        icon: 'circle-help',
        command: () => {
          void this.guidedOnboarding.replay();
        }
      });
    }

    if (this.guidedOnboarding.contextualTourId()) {
      accountItems.push({
        id: 'help-tour',
        label: 'Conhecer esta tela',
        icon: 'circle-help',
        command: () => {
          void this.guidedOnboarding.replayContextual();
        }
      });
    }

    accountItems.push(
      {
        separator: true
      },
      {
        label: 'Sair',
        icon: 'log-out',
        styleClass: 'logout-menu-item',
        command: () => {
          this.handleLogout();
        }
      }
    );

    return [
      {
        label: 'Conta',
        items: accountItems
      }
    ];
  });

  private handleLogout(): void {
    this.authFacade.logout();
  }
}
